<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <div class="page-header">
        <div class="row align-items-center">
          <div class="col">
            <h3 class="page-title">Thiết bị nhận thông báo</h3>
            <ul class="breadcrumb">
              <li class="breadcrumb-item"><router-link to="/">Dashboard</router-link></li>
              <li class="breadcrumb-item active">Quản lý thiết bị</li>
            </ul>
          </div>
          <div class="col-auto">
            <div class="actions-group">
              <button class="btn btn-outline-primary" @click="refresh" :disabled="isLoading">
                <i class="fa fa-sync-alt"></i> Tải lại
              </button>
              <button class="btn btn-primary" :disabled="!isPushEnabled || isLoading" @click="subscribeCurrentDevice">
                <i class="fa fa-plus"></i> Thêm thiết bị này
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="!isPushEnabled" class="alert alert-danger">
        <i class="fas fa-exclamation-triangle"></i> Trình duyệt của bạn không hỗ trợ hoặc đã chặn tính năng Push Notification.
      </div>

      <div v-if="isLoading" class="device-grid">
        <div v-for="i in 3" :key="i" class="device-card skeleton">
          <div class="skeleton-icon"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-text short"></div>
          <div class="skeleton-button"></div>
        </div>
      </div>
      
      <div v-else-if="devices.length > 0" class="device-grid">
        <div v-for="device in devices" :key="device.endpoint" class="device-card" :class="{ 'current-device': isCurrentDevice(device) }">
          <div class="device-icon-wrapper">
            <i :class="getBrowserIcon(device.userAgent)"></i>
            <i :class="getOsIcon(device.userAgent)" class="os-icon"></i>
          </div>
          <div class="device-info">
            <h5 class="device-name">
              {{ getFriendlyDeviceName(device.userAgent) }}
              <span v-if="isCurrentDevice(device)" class="badge badge-success ml-2">Thiết bị này</span>
            </h5>
            <p class="device-meta" :title="device.userAgent">
              Đăng ký lần cuối: {{ formatSpecificDTNative(device.createdAt) }}
            </p>
          </div>
          <div class="device-actions">
            <button class="btn btn-sm btn-outline-danger" @click="remove(device.endpoint)">
              <i class="fas fa-trash-alt"></i> Hủy
            </button>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="fas fa-mobile-alt empty-icon"></i>
        <h4 class="mt-3">Chưa có thiết bị nào được đăng ký</h4>
        <p class="text-muted">Hãy thêm thiết bị hiện tại của bạn để bắt đầu nhận thông báo.</p>
        <button class="btn btn-primary mt-3" :disabled="!isPushEnabled" @click="subscribeCurrentDevice">
          <i class="fas fa-plus"></i> Thêm thiết bị này ngay!
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue';
import { usePushStore } from '@/stores/pushStore';

export default {
  name: 'PushDevicesView',
  setup() {
    const store = usePushStore();
    const devices = computed(() => store.devices);
    const isLoading = computed(() => store.isLoading);
    const isPushEnabled = computed(() => store.isPushEnabled);
    
    // Hàm nhận diện trình duyệt từ User Agent
    const getBrowserIcon = (ua = '') => {
      ua = ua.toLowerCase();
      if (ua.includes('firefox')) return 'fa fa-firefox-browser';
      if (ua.includes('edg')) return 'fa fa-edge';
      if (ua.includes('chrome')) return 'fa fa-chrome';
      if (ua.includes('safari')) return 'fa fa-safari';
      return 'fa fa-globe'; // Icon mặc định
    };
    
    // Hàm nhận diện hệ điều hành từ User Agent
    const getOsIcon = (ua = '') => {
      ua = ua.toLowerCase();
      if (ua.includes('windows')) return 'fa fa-windows';
      if (ua.includes('macintosh') || ua.includes('mac os')) return 'fa fa-apple';
      if (ua.includes('android')) return 'fa fa-android';
      if (ua.includes('linux')) return 'fa fa-linux';
      return 'fa fa-desktop'; // Icon mặc định
    };

    // Hàm tạo tên thân thiện
    const getFriendlyDeviceName = (ua = '') => {
        const browser = getBrowserIcon(ua).split(' ')[1].replace('fa-', '').replace('-browser', '');
        const os = getOsIcon(ua).split(' ')[1].replace('fa-', '');
        const friendlyBrowser = browser.charAt(0).toUpperCase() + browser.slice(1);
        const friendlyOs = os.charAt(0).toUpperCase() + os.slice(1);
        return `${friendlyBrowser} trên ${friendlyOs}`;
    };

    // Hàm kiểm tra thiết bị hiện tại
    const isCurrentDevice = (device) => {
        return store.currentSubscription && device.endpoint === store.currentSubscription.endpoint;
    };
    
    // Hàm format ngày tháng đẹp hơn (ví dụ: 2 giờ trước)
    const formatSpecificDTNative = (dateString) => {
      if (!dateString) return '—';

      const date = new Date(dateString);

      // Các tùy chọn format
      const options = {
        timeZone: 'Asia/Ho_Chi_Minh', // Múi giờ Việt Nam
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour12: false // Dùng định dạng 24h
      };

      // Sử dụng Intl.DateTimeFormat để lấy các phần của ngày tháng
      const formatter = new Intl.DateTimeFormat('vi-VN', options);
      const parts = formatter.formatToParts(date);
      
      // Dùng .find() để lấy giá trị của từng phần một cách an toàn
      const partValue = (type) => parts.find(part => part.type === type)?.value || '';

      const day = partValue('day');
      const month = partValue('month');
      const year = partValue('year');
      const hour = partValue('hour');
      const minute = partValue('minute');

      // Ghép lại thành chuỗi mong muốn
      return `${hour}:${minute} ngày ${day}/${month}/${year}`;
    };

    
    const refresh = async () => await store.fetchDevices();
    const remove = async (endpoint) => await store.removeDevice(endpoint);
    const subscribeCurrentDevice = async () => await store.subscribeCurrentDevice();

    onMounted(async () => {
      // Gọi cả 2 hàm này để có đủ thông tin
      await store.checkSubscriptionStatus(); 
      await store.fetchDevices();
    });

    return { 
      devices, 
      isLoading, 
      isPushEnabled, 
      refresh, 
      remove, 
      subscribeCurrentDevice, 
      formatSpecificDTNative,
      getBrowserIcon,
      getOsIcon,
      getFriendlyDeviceName,
      isCurrentDevice
    };
  },
};
</script>

<style scoped>
/* --- Cấu trúc lưới (Grid) --- */
.device-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* --- Thẻ thông tin thiết bị --- */
.device-card {
  background-color: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.device-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}
.device-card.current-device {
  border-color: #16a34a; /* Màu xanh lá cho thiết bị hiện tại */
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2);
}

/* --- Icon --- */
.device-icon-wrapper {
  position: relative;
  font-size: 2.5rem; /* Icon trình duyệt to */
  color: #334155;
  flex-shrink: 0;
}
.os-icon {
  position: absolute;
  bottom: -4px;
  right: -8px;
  font-size: 1rem; /* Icon HĐH nhỏ */
  background: white;
  border-radius: 50%;
  padding: 2px;
}

/* --- Thông tin thiết bị --- */
.device-info {
  flex-grow: 1;
  overflow: hidden;
}
.device-name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}
.device-meta {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- Nút hành động --- */
.device-actions {
  flex-shrink: 0;
}
.actions-group {
  display: flex;
  gap: 0.5rem;
}

/* --- Skeleton Loading --- */
.skeleton {
  cursor: progress;
}
.skeleton-icon { width: 48px; height: 48px; border-radius: 50%; background-color: #e2e8f0; animation: pulse 1.5s infinite; }
.skeleton-text { width: 80%; height: 20px; border-radius: 8px; background-color: #e2e8f0; margin-top: 8px; animation: pulse 1.5s infinite; }
.skeleton-text.short { width: 50%; }
.skeleton-button { display: none; } /* Không cần button cho skeleton */

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* --- Empty State --- */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background-color: #f8f9fa;
  border-radius: 12px;
  text-align: center;
  min-height: 400px;
}
.empty-icon {
  font-size: 4rem;
  color: #adb5bd;
}
</style>
